import { PageHeader } from "@ant-design/pro-layout";
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Layout,
  Modal,
  Row,
  Space,
  Typography,
  theme,
} from "antd";
import firebase from "firebase";
import axios from "axios";
import { Content } from "antd/es/layout/layout";
import React, { useEffect } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  materialDark,
  materialLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { CustomParamsInput } from "./components/CustomParamsInput";
import { OrientationSelect } from "./components/OrientationSelect";
import { CacheTypeSelect } from "./components/CacheTypeSelect";
import { PhaseList } from "./components/Phase/PhaseList";
import { FileOutlined } from "@ant-design/icons";
import { CreativesListComponent } from "../components/CreativesListComponent";
import { StorageItem } from "../list";
import { initialAdaptiveRendering } from "./types";

const { useToken } = theme;

export const AdaptiveRenderingPage: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [template, setTemplate] = React.useState<string>("");
  const [path, setPath] = React.useState<string>("");

  const { token } = useToken();
  const isDarkTheme = token.colorBgBase === "#000";
  const [isSelectCreativeVisible, setSelectCreativeVisible] =
    React.useState(false);
  const [isSaveCreativeDialogVisible, setSaveCreativeDialogVisible] =
    React.useState(false);

  const loadDocument = (item: StorageItem) => {
    item.ref.getDownloadURL().then((url) => {
      axios
        .get(url)
        .then((response) => {
          console.log(response.data);
          form.setFieldsValue(response.data);
          setPath(`/${item.ref.fullPath}`);
        })
        .finally(() => setSelectCreativeVisible(false));
    });
  };

  const handleFormChange = (changedValues: any, allValues: any) => {
    setTemplate(JSON.stringify(allValues, null, 2));
  };

  useEffect(() => {
    setTemplate(JSON.stringify(form.getFieldsValue(), null, 2));
  }, [form]);

  return (
    <Layout>
      <Content>
        <PageHeader
          title="Adaptive Rendering"
          extra={<Typography.Text type="secondary">{path}</Typography.Text>}
        />
        <Row gutter={[16, 16]}>
          <Col span={16}>
            <Form
              form={form}
              initialValues={initialAdaptiveRendering}
              onValuesChange={handleFormChange}
              onFinish={(_) => setSaveCreativeDialogVisible(true)}
              layout="vertical"
            >
              <Form.Item label="Orientation" name="orientation">
                <OrientationSelect />
              </Form.Item>
              <Form.Item label="Cache Type" name="cache_type">
                <CacheTypeSelect />
              </Form.Item>
              <Form.Item label="Phases">
                <PhaseList phases={form.getFieldsValue().phases} />
              </Form.Item>
              <Form.Item label="Custom Parameters" name="custom_params">
                <CustomParamsInput />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col span={8}>
            <Row gutter={[16, 16]}>
              <Space direction="vertical" size="large">
                <Button
                  type="primary"
                  icon={<FileOutlined  />}
                  onClick={(_) => setSelectCreativeVisible(true)}
                >
                  Open
                </Button>
              </Space>
            </Row>
            <Row gutter={[16, 16]}>
              <SyntaxHighlighter
                style={isDarkTheme ? materialDark : materialLight}
                language="javascript"
              >
                {template}
              </SyntaxHighlighter>
            </Row>
          </Col>
        </Row>
        <Drawer
          title="Select Creative"
          placement="right"
          closable={true}
          onClose={() => setSelectCreativeVisible(false)}
          open={isSelectCreativeVisible}
          width={500}
        >
          <CreativesListComponent onItemClick={loadDocument} />
        </Drawer>
        <Modal
          title="Save Creative"
          open={isSaveCreativeDialogVisible}
          okButtonProps={{ form: "save-creative-form", htmlType: "submit" }}
          onCancel={() => setSaveCreativeDialogVisible(false)}
        >
          <Form
            id="save-creative-form"
            onFinish={(values) => {
              firebase
                .storage()
                .ref(`/Adaptive Rendering/${values.filename}.json`)
                .putString(JSON.stringify(form.getFieldsValue()));
              setSaveCreativeDialogVisible(false);
            }}
            layout="vertical"
          >
            <Form.Item label="File Name" name="filename">
              <Input addonAfter=".json" />
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};
